import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { SubmitButton } from "components/common/buttons/SubmitButton";
import { Select } from "components/common/Select";
import { fetchLanguages } from "slices/languageSlice";
import { languageSelector } from "slices/languageSlice";
import { setError } from "slices/dashboardSlice";
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';
import FullPageLoader from "components/common/FullPageLoader";
import ListTable from "components/interpreterAccess/dashboard/ListTable";
import { InterpreterDashboardSelector } from "slices/interpreter/dashboardSlice";
import { fetchInterpreterDashboardAnalytics } from "slices/interpreter/dashboardSlice";
import { fetchInterpreterDashboardAppointments, downloadDashboardReport } from "slices/interpreter/dashboardSlice";
import { interpreterLegends } from "components/common/TableColor";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { fetchInterpreterTranslationsList } from "slices/interpreter/interpreterTranslationSlice";
import TranslationListTable from "components/dashboard/TranslationListTable";


function Dashboard() {
  const dispatch = useDispatch()
  const { interpreterDashboard: dashboard, last_page: totalPages, loader, total } = useSelector(InterpreterDashboardSelector);
  const { language: languages } = useSelector(languageSelector);
  const [activeLegend, setActiveLegend] = useState(0);
  const [downloading, setDownloading] = useState(false);
  const [statusValue, setStatusValue] = React.useState('all');
  const navigate = useNavigate();
   const defaultValues = {
      status: 'all',
      start_date: moment().format('YYYY-MM-DD'),
      end_date: moment().format('YYYY-MM-DD'),
    };
  const translationsState = useSelector((state) => state.interpreterTranslators) || {};
  const { 
  interpreterTranslations = { data: [], total: 0 }, 
  total: totalTranslations = 0, 
  current_page = 1, 
  per_page = 10 
} = translationsState;
const translations = interpreterTranslations;
  const [activeTab, setActiveTab] = useState('appointments');
  const { clearErrors, handleSubmit, watch, formState: { errors }, control, setValue, setError: setErrors, reset, getValues } = useForm({
    defaultValues: defaultValues
  });
  const [filters, setFilters] = useState(defaultValues);


  
    useEffect(() => {
      dispatch(fetchInterpreterDashboardAnalytics());  
      const defaultTab = 'appointments';
      setActiveTab(defaultTab);
  
      let apiParams = {
        pageLength: 5,
        sortBy: "id",
        sortDirection: "desc"
      };
  
      if (defaultTab === 'appointments') {
        apiParams = { ...apiParams, type: [0, 1] };
        dispatch(fetchInterpreterDashboardAppointments(1, apiParams));
      }
    }, [dispatch]);


  // #region Language dropdown 
  const filteredLanguages = React.useMemo(() =>
    languages?.map(language => {
      const object = {
        value: language?.id,
        label: language?.name
      }
      return object
    }), [languages]);

  const filteredLanguagesLoadOptions = async (inputValue, loadedOptions, { page }) => {
    const result = await dispatch(fetchLanguages(page, { pageLength: 20, sortBy: "id", sortDirection: "asc", search: inputValue }, true, true))
    return {
      options: result.data.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      hasMore: page !== result?.last_page ? true : false,
      additional: {
        page: page + 1,
      },
    };
  };
  //#endregion


  // #region Legends 
  const filteredStatuses = React.useMemo(() => {
    const temp_array = [...interpreterLegends];
    temp_array.unshift({
      value: "all",
      name: "All",
      id: 0,
      color: "#3176A0"
    })
    return temp_array;
  }, [interpreterLegends]);

  const hanldeActiveLegend = (id, value) => {
    setActiveLegend(id);
    dispatch(fetchInterpreterDashboardAppointments(1, { pageLength: 10, status: value, start_date: moment(watch('start_date'))?.format('YYYY-MM-DD'), end_date: moment(watch('end_date'))?.format('YYYY-MM-DD'), sortBy: "id", sortDirection: "desc" }))
    setStatusValue(value)
  }

  //#endregion


  const onSubmit = (data) => {
    dispatch(setError({}))
    data = {
      ...data,
      status: statusValue,
    }
    setFilters(data);
    dispatch(fetchInterpreterDashboardAppointments(1, data))
    clearErrors();
  };


  const handleResetFields = () => {
    setValue("start_date", "");
    setValue("end_date", "");
    setValue("language_id", "");
    setValue("account", "");
    setValue("department", "");
    setValue("facility", "");
    setValue("service", "");
    dispatch(fetchInterpreterDashboardAppointments(1, { sortBy: "status", sortDirection: "asc" }))
    clearErrors()
  };

  const downloadBlobFile = (blob, filename = "appointments.xlsx") => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };


  const handleDownloadReport = () => {
    setDownloading(true);


    const filters = {
      ...getValues(),
      status: statusValue
    };

    dispatch(
      downloadDashboardReport(filters, (success, res) => {
        setDownloading(false);

        if (!success) {
          Swal.fire({
            title: "Error!",
            text: (res && res.message) || "Failed to download report",
            icon: "error",
            confirmButtonColor: "#d33",
          });
          return;
        }

        try {
          if (res instanceof Blob) {
            downloadBlobFile(res);
          } else if (res && res.blob) {
            downloadBlobFile(res.blob, res.filename || "dashboard_report.xlsx");
          } else if (typeof res === "string" && res.startsWith("data:")) {
            const arr = res.split(",");
            const mimeMatch = arr[0].match(/:(.*?);/);
            const mime = mimeMatch ? mimeMatch[1] : "application/octet-stream";
            const bstr = atob(arr[1]);
            let n = bstr.length;
            const u8 = new Uint8Array(n);
            while (n--) u8[n] = bstr.charCodeAt(n);
            const blob = new Blob([u8], { type: mime });
            downloadBlobFile(blob);
          } else if (typeof res === "string") {
            const a = document.createElement("a");
            a.href = res;
            a.target = "_blank";
            a.download = "";
            document.body.appendChild(a);
            a.click();
            a.remove();
          }

          Swal.fire({
            title: "Success!",
            text: "Report downloaded successfully.",
            icon: "success",
            confirmButtonColor: "#3085d6",
          });
        } catch (err) {
          console.error("Download error:", err);
          Swal.fire({
            title: "Error!",
            text: "Could not process downloaded file",
            icon: "error",
            confirmButtonColor: "#d33",
          });
        }
      })
    );
  };


  const serviceOption = [
    { value: 'In Person', label: 'In Person' },
    { value: 'Phone', label: 'Phone' },
    { value: 'Online Video Call (Zoom, Google Meet, or GotoMeeting)', label: 'Online Video Call (Zoom, Google Meet, or GotoMeeting)' },
    { value: 'Online Avant (for certified positions)', label: 'Online Avant (for certified positions)' },
    { value: 'Bilingual assistant/paraprofessional', label: 'Bilingual assistant/paraprofessional' },
    { value: 'Interpreter/Translator Specialist', label: 'Interpreter/Translator Specialist' },
    { value: 'Police Officer', label: 'Police Officer' },
    { value: 'Braille', label: 'Braille' },
  ]

  const [isFourteenDays, setIsFourteenDays] = useState(false);
  const tableRef = useRef(null);

  const handleFilter = (item, isforteen) => {
    const today = moment().format('YYYY-MM-DD');
    const before_fourteen_days = moment().subtract(14, 'days').format('YYYY-MM-DD');

    if (isforteen) {
      setValue("start_date", before_fourteen_days);
    } else {
      setValue("start_date", today);
    }

    setValue("end_date", today);
    if (item?.value) {
      setValue("service", item?.value)
    } else {
      setValue("service", null)
    }

    const data = {
      start_date: today,
      end_date: today,
      service: item?.value || "",
    };

    dispatch(fetchInterpreterDashboardAppointments(1, data));
    tableRef.current.scrollIntoView({
      behavior: "smooth",
    });
    dispatch(setError({}));
    clearErrors();
  };

  const viewAppointments = (type) => {
    if (type === 0 || type === 1) {
      navigate("/interpreter/appointments", {
        state: {
          filterByToday: true,
          type: type,
        },
      });
    }
    else if (type === 2) {
      navigate("/interpreter/billingual-requests", {
        state: {
          filterByToday: true,
        },
      });
    }
    else if (type === 3) {
      navigate("/interpreter/translations", {
        state: {
          filterByToday: true,
        },
      });
    }
  };
  const nextFourteenDaysAppointments = (type) => {
    if (type === 0 || type === 1) {
      navigate("/interpreter/appointments", {
        state: {
          filterByFourteen: true,
          type: type,
        },
      });
    }
    else if (type === 2) {
      navigate("/interpreter/billingual-requests", {
        state: {
          filterByFourteen: true,
        },
      });
    }
    else if (type === 3) {
      navigate("/interpreter/translations", {
        state: {
          filterByFourteen: true,
        },
      });
    }
  };
   const fetchList = (page = 1, params = {}) => {
      dispatch(fetchInterpreterTranslationsList(page, params));
    };


    const handleTabClick = (tabType) => {
      setActiveTab(tabType);
  
      let apiParams = {
        pageLength: 5,
        sortBy: "id",
        sortDirection: "desc"
      };
  
      if (tabType === 'appointments') {
        apiParams = { ...apiParams, type: [0, 1] };
        dispatch(fetchInterpreterDashboardAppointments(1, apiParams));
      } else if (tabType === 'bilingual') {
        apiParams = { ...apiParams, type: 2 };
        dispatch(fetchInterpreterDashboardAppointments(1, apiParams));
      } else if (tabType === 'translations') {
        dispatch(fetchInterpreterTranslationsList(1, apiParams));
      }
    };



  return (
    <>
      <div className="content">
        <div className="row_main_head">
          <h2 className="main_heading">Dashboard</h2>
        </div>

        <div className="row_dash_1">
          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Today</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.todays_data?.map((service, index) => (
                    <tr
                      className="cursorPointer"
                      key={index}
                      onClick={() => {
                        viewAppointments(service?.value);
                      }}
                    >
                      <td>{service?.type}</td>
                      <td>{service?.assigned}</td>
                      <td>{service?.pending}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card_table">
            <div className="interpreter p-4">
              <h2>Next 14 Days</h2>
            </div>

            <div className="Table_data_show pt-0">
              <table>
                <thead>
                  <tr>
                    <th className="col-6">Today</th>
                    <th className="col-3 text-center">Registered</th>
                    <th className="col-3 text-center">To Cover</th>
                  </tr>
                </thead>
                <tbody>
                  {dashboard?.analytics?.fourteen_days?.map((service, index) => (
                    <tr
                      className="cursorPointer"
                      key={index}
                      onClick={() => {
                        nextFourteenDaysAppointments(service?.value);
                      }}
                    >
                      <td>{service?.type}</td>
                      <td>{service?.assigned}</td>
                      <td>{service?.pending}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* <div className="card_table">
          <div className="button-sm forLegends p-3 gap-1">
            <LegendButton activeId={activeLegend} options={filteredStatuses} handleClick={hanldeActiveLegend} />
          </div>
          <form className="search_form_ad">
            <div className="row-primary dahboard_filter">
              <DateRangeFilter control={control} errors={errors} />
              <div className="row-primary">
                <h2>Clients</h2>
                <Controller
                  name="account"
                  control={control}
                  render={({ field }) =>
                    <div className="input-basis" style={{ width: "100%" }}>
                      <input className="input-in type_inp" name="account" type="text" placeholder="Client name" {...field} />
                    </div>
                  }
                />
              </div>
              <div className="row-primary">
                <h2>Language</h2>
                <Controller
                  name="language_id"
                  control={control}
                  render={({ field }) =>
                    <div style={{ width: "100%" }}>
                      <AsyncPaginate
                        menuPlacement='auto'
                        styles={{
                          ...selectCustomStyles,
                        }}
                        value={field.value ? filteredLanguages?.find((opt) => field.value == opt.value) : ""}
                        loadOptions={filteredLanguagesLoadOptions}
                        isClearable
                        escapeClearsValue
                        onChange={(obj) => {
                          field.onChange(obj?.value);
                        }}
                        additional={{
                          page: 1,
                        }}
                      />
                    </div>
                  }
                />
              </div>
              <div className="row-primary">
                <h2>Meeting Format</h2>

                <Controller
                  name="service"
                  control={control}
                  render={({ field }) =>
                    <div style={{ width: "100%" }}>
                      <Select
                        name="service"
                        options={serviceOption || []}
                        {...field}
                        value={field.value ? serviceOption?.find((opt) => field.value == opt.value) : ""}
                      />
                    </div>
                  }
                />
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-between btns_area">
            <div className="row-primary row_10">
              <SubmitButton handleSubmit={handleSubmit(onSubmit)} buttonType="button" btnText="Search" className={'green'} />
              <SubmitButton buttonType="button" className={'lightBlue'} btnText="Reset" handleSubmit={handleResetFields} />
            </div>
              <button
                type="button"
                style={{ height: "fit-content", marginLeft: "10px" }}
                className="main_button"
                onClick={handleDownloadReport}
              >
                Export to Excel
              </button>
              </div>

            <div ref={tableRef}>
              <ListTable data={dashboard?.appointments} filtration={filters}/>
            </div>

          </form>
        </div> */}

        <div className="card_table p-4">
          {/* Tabs */}
          <div className="tabs_wrapper" data-active={activeTab}>
            {[
              { key: "appointments", label: "Appointments" },
              { key: "bilingual", label: "Bilingual Exams" },
              { key: "translations", label: "Translations" },
            ].map((tab) => (
              <div className="border_btn" key={tab.key}>
                <button
                  className={`button-bu ${activeTab === tab.key ? "active" : ""}`}
                  onClick={() => handleTabClick(tab.key)}
                >
                  <p>{tab.label}</p>
                </button>
              </div>
            ))}
          </div>

          <div className="mt-4">
            {activeTab === "appointments" &&
              <div ref={tableRef}>
                <ListTable data={dashboard?.appointments} filtration={filters} hidePagination={true} hideHeader={true} heading='Latest Appointments' link='appointments' />
              </div>}
            {activeTab === "bilingual" &&
              <div ref={tableRef}>
                <ListTable data={dashboard?.appointments} filtration={filters} hidePagination={true} hideHeader={true} heading='Latest Bilingual Exams' link='billingual-requests' />
              </div>}
            {activeTab === "translations" &&
              <div ref={tableRef}>
                <TranslationListTable
                  translator={false}
                  link='interpreter/translation'
                  translations={translations?.data || []}
                  total={totalTranslations}
                  currentPage={current_page}
                  pageLength={per_page}
                  fetchList={fetchList}
                  hideHeader={true}
                  hidePagination={true}
                />

              </div>}
          </div>
        </div>

      </div>

  {(loader || translationsState.loader) && <FullPageLoader />}
    </>
  );
}

export default Dashboard;